import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import api from '../../services/api';
import { CouponResponse, CouponCreate, CouponUpdate, OrderProduct } from '../../types/order';

function fmt(v: number) {
  return `$${Number(v).toFixed(2)}`;
}

interface CouponFormState {
  code: string;
  description: string;
  discount_amount: string;
  discount_months: string;
  max_uses: string;
  valid_from: string;
  valid_until: string;
  is_active: boolean;
  applicable_product_ids: string[];
  per_plan: boolean;
  plan_amounts: Record<string, string>;
  discount_contract_types: string[];
  bonus_title: string;
  bonus_description: string;
  bonus_terms: string;
  bonus_contract_types: string[];
}

const CONTRACT_OPTIONS = [
  { value: 'no_contract', label: 'No contract' },
  { value: '12_month', label: '12 months' },
  { value: '24_month', label: '24 months' },
];

function ContractChips({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {CONTRACT_OPTIONS.map((o) => {
        const on = value.includes(o.value);
        return (
          <Chip
            key={o.value}
            label={o.label}
            size="small"
            clickable
            color={on ? 'primary' : 'default'}
            variant={on ? 'filled' : 'outlined'}
            onClick={() => onChange(on ? value.filter((x) => x !== o.value) : [...value, o.value])}
          />
        );
      })}
    </Box>
  );
}

const EMPTY_FORM: CouponFormState = {
  code: '',
  description: '',
  discount_amount: '',
  discount_months: '6',
  max_uses: '',
  valid_from: '',
  valid_until: '',
  is_active: true,
  applicable_product_ids: [],
  per_plan: false,
  plan_amounts: {},
  discount_contract_types: [],
  bonus_title: '',
  bonus_description: '',
  bonus_terms: '',
  bonus_contract_types: [],
};

function CouponFormDialog({
  open,
  editCoupon,
  onClose,
  onSaved,
}: {
  open: boolean;
  editCoupon: CouponResponse | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<CouponFormState>(EMPTY_FORM);
  const [products, setProducts] = useState<OrderProduct[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.getOrderProducts().then(setProducts).catch(() => {});
  }, []);

  useEffect(() => {
    if (editCoupon) {
      setForm({
        code: editCoupon.code,
        description: editCoupon.description || '',
        discount_amount: editCoupon.discount_amount != null ? String(editCoupon.discount_amount) : '',
        discount_months: String(editCoupon.discount_months),
        max_uses: editCoupon.max_uses != null ? String(editCoupon.max_uses) : '',
        valid_from: editCoupon.valid_from ? editCoupon.valid_from.slice(0, 16) : '',
        valid_until: editCoupon.valid_until ? editCoupon.valid_until.slice(0, 16) : '',
        is_active: editCoupon.is_active,
        applicable_product_ids: editCoupon.applicable_product_ids || [],
        discount_contract_types: editCoupon.discount_contract_types || [],
        bonus_title: editCoupon.bonus_title || '',
        bonus_description: editCoupon.bonus_description || '',
        bonus_terms: editCoupon.bonus_terms || '',
        bonus_contract_types: editCoupon.bonus_contract_types || [],
        per_plan: !!editCoupon.plan_discounts?.length,
        plan_amounts: Object.fromEntries(
          (editCoupon.plan_discounts || []).map((d) => [d.product_id, String(d.discount_amount)])
        ),
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setError(null);
  }, [editCoupon, open]);

  const set = (field: keyof CouponFormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const toggleProduct = (id: string) => {
    setForm((f) => ({
      ...f,
      applicable_product_ids: f.applicable_product_ids.includes(id)
        ? f.applicable_product_ids.filter((x) => x !== id)
        : [...f.applicable_product_ids, id],
    }));
  };

  const planDiscounts = Object.entries(form.plan_amounts)
    .filter(([, v]) => v !== '' && parseFloat(v) > 0)
    .map(([product_id, v]) => ({ product_id, discount_amount: parseFloat(v) }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        description: form.description.trim() || undefined,
        discount_amount: form.per_plan || !form.discount_amount ? undefined : parseFloat(form.discount_amount),
        discount_contract_types: form.discount_contract_types,
        bonus_title: form.bonus_title.trim() || undefined,
        bonus_description: form.bonus_description.trim() || undefined,
        bonus_terms: form.bonus_terms.trim() || undefined,
        bonus_contract_types: form.bonus_contract_types,
        plan_discounts: form.per_plan ? planDiscounts : undefined,
        discount_months: parseInt(form.discount_months, 10),
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : undefined,
        valid_from: form.valid_from ? new Date(form.valid_from).toISOString() : undefined,
        valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : undefined,
        is_active: form.is_active,
        applicable_product_ids: !form.per_plan && form.applicable_product_ids.length > 0 ? form.applicable_product_ids : undefined,
      };
      if (editCoupon) {
        const update: CouponUpdate = { ...payload };
        if (form.per_plan) update.plan_discounts = planDiscounts;
        else update.plan_discounts = [];
        if (!form.max_uses) update.max_uses = null;
        if (!form.bonus_title.trim()) {
          update.bonus_title = null;
          update.bonus_description = null;
          update.bonus_terms = null;
        }
        if (!form.discount_amount && !form.per_plan) update.discount_amount = undefined;
        if (!form.valid_from) update.valid_from = null;
        if (!form.valid_until) update.valid_until = null;
        await api.updateCoupon(editCoupon.id, update);
      } else {
        await api.createCoupon(payload as CouponCreate);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to save coupon.');
    } finally {
      setSaving(false);
    }
  };

  const internetPlans = products.filter((p) => p.product_type === 'internet_plan');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editCoupon ? 'Edit Coupon' : 'Create Coupon'}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Code"
            value={form.code}
            onChange={set('code')}
            disabled={!!editCoupon}
            required
            inputProps={{ style: { textTransform: 'uppercase' } }}
            helperText="Customers enter this at checkout"
          />
          <TextField
            label="Description (internal)"
            value={form.description}
            onChange={set('description')}
            placeholder="e.g. $10 off for new Supa customers"
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.per_plan}
                onChange={(e) => setForm((f) => ({ ...f, per_plan: e.target.checked }))}
              />
            }
            label="Different discount per plan"
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            {!form.per_plan && (
              <TextField
                label="Discount amount ($)"
                type="number"
                value={form.discount_amount}
                onChange={set('discount_amount')}
                helperText="Blank if this coupon only gives a bonus"
                inputProps={{ min: 0.01, step: 0.01 }}
                sx={{ flex: 1 }}
              />
            )}
            <TextField
              label="Duration (months)"
              type="number"
              value={form.discount_months}
              onChange={set('discount_months')}
              required
              inputProps={{ min: 1, step: 1 }}
              sx={{ flex: 1 }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Max uses (blank = unlimited)"
              type="number"
              value={form.max_uses}
              onChange={set('max_uses')}
              inputProps={{ min: 1, step: 1 }}
              sx={{ flex: 1 }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Valid from"
              type="datetime-local"
              value={form.valid_from}
              onChange={set('valid_from')}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Valid until"
              type="datetime-local"
              value={form.valid_until}
              onChange={set('valid_until')}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
          </Box>

          {form.per_plan && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Monthly discount per plan (leave blank = coupon doesn't apply to that plan)
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {internetPlans.map((p) => (
                  <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {p.name}{p.monthly_price != null ? ` (${fmt(p.monthly_price)}/mo)` : ''}
                    </Typography>
                    <TextField
                      size="small"
                      type="number"
                      label="Off ($/mo)"
                      value={form.plan_amounts[p.id] ?? ''}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, plan_amounts: { ...f.plan_amounts, [p.id]: e.target.value } }))
                      }
                      inputProps={{ min: 0.01, step: 0.01 }}
                      sx={{ width: 130 }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {(form.per_plan || form.discount_amount) && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Discount only applies with this contract (none selected = any contract)
              </Typography>
              <ContractChips
                value={form.discount_contract_types}
                onChange={(v) => setForm((f) => ({ ...f, discount_contract_types: v }))}
              />
            </Box>
          )}

          <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle2">Bonus extra (optional)</Typography>
            <TextField
              label="Bonus title"
              value={form.bonus_title}
              onChange={set('bonus_title')}
              placeholder="e.g. $150 Gift Voucher"
              helperText="Leave blank for no bonus. A coupon can have a bonus, a discount, or both."
            />
            {form.bonus_title.trim() && (
              <>
                <TextField
                  label="Bonus description"
                  value={form.bonus_description}
                  onChange={set('bonus_description')}
                  multiline
                  minRows={2}
                />
                <TextField
                  label="Bonus terms and conditions (optional)"
                  value={form.bonus_terms}
                  onChange={set('bonus_terms')}
                  multiline
                  minRows={3}
                  helperText="Shown on the order summary, confirmation screen, approval page and emails, and the order PDF."
                />
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Bonus only with this contract (none selected = any contract)
                  </Typography>
                  <ContractChips
                    value={form.bonus_contract_types}
                    onChange={(v) => setForm((f) => ({ ...f, bonus_contract_types: v }))}
                  />
                </Box>
              </>
            )}
          </Box>

          {!form.per_plan && internetPlans.length > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Applicable plans (leave all unchecked = any plan)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {internetPlans.map((p) => (
                  <Chip
                    key={p.id}
                    label={p.name}
                    onClick={() => toggleProduct(p.id)}
                    color={form.applicable_product_ids.includes(p.id) ? 'primary' : 'default'}
                    variant={form.applicable_product_ids.includes(p.id) ? 'filled' : 'outlined'}
                    clickable
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={form.is_active}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
              />
            }
            label="Active"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || !form.code || !form.discount_months || (form.per_plan ? planDiscounts.length === 0 : !form.discount_amount && !form.bonus_title.trim())}>
          {saving ? <CircularProgress size={20} /> : editCoupon ? 'Save changes' : 'Create coupon'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function CouponsPage() {
  const [coupons, setCoupons] = useState<CouponResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCoupon, setEditCoupon] = useState<CouponResponse | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<CouponResponse | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.listCoupons();
      setCoupons(data);
      setError(null);
    } catch {
      setError('Failed to load coupons.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await api.deleteCoupon(deleteConfirm.id);
      setDeleteConfirm(null);
      load();
    } catch {
      setError('Failed to delete coupon.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Coupon Codes</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setEditCoupon(null); setDialogOpen(true); }}
        >
          Create coupon
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Code</strong></TableCell>
                <TableCell><strong>Discount</strong></TableCell>
                <TableCell><strong>Plans</strong></TableCell>
                <TableCell><strong>Uses</strong></TableCell>
                <TableCell><strong>Valid</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coupons.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No coupons yet. Create one to get started.
                  </TableCell>
                </TableRow>
              )}
              {coupons.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>
                    <Typography fontWeight={700} fontFamily="monospace">{c.code}</Typography>
                    {c.description && (
                      <Typography variant="caption" color="text.secondary">{c.description}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {c.plan_discounts?.length
                      ? `${c.plan_discounts.map((d) => fmt(d.discount_amount)).join(' / ')} per plan/mo × ${c.discount_months} months`
                      : c.discount_amount != null
                      ? `${fmt(Number(c.discount_amount))}/mo × ${c.discount_months} months`
                      : null}
                    {c.bonus_title && (
                      <Typography variant="caption" display="block" color="primary">
                        + {c.bonus_title}
                        {c.bonus_contract_types?.length ? ` (${c.bonus_contract_types.map((t) => t.replace('_', ' ')).join(' / ')})` : ''}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {c.plan_discounts?.length
                      ? <Typography variant="caption">{c.plan_discounts.length} plan(s), per-plan</Typography>
                      : c.applicable_product_ids?.length
                      ? <Typography variant="caption">{c.applicable_product_ids.length} plan(s)</Typography>
                      : <Typography variant="caption" color="text.secondary">Any plan</Typography>}
                  </TableCell>
                  <TableCell>
                    {c.uses_count}{c.max_uses != null ? ` / ${c.max_uses}` : ''}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {c.valid_from ? new Date(c.valid_from).toLocaleDateString() : '—'}
                      {' → '}
                      {c.valid_until ? new Date(c.valid_until).toLocaleDateString() : 'no expiry'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={c.is_active ? 'Active' : 'Inactive'}
                      color={c.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => { setEditCoupon(c); setDialogOpen(true); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDeleteConfirm(c)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CouponFormDialog
        open={dialogOpen}
        editCoupon={editCoupon}
        onClose={() => setDialogOpen(false)}
        onSaved={load}
      />

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete coupon?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete coupon <strong>{deleteConfirm?.code}</strong>?
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
